'use client';

import type { CSSProperties, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';

const PORTAL_MARKERS = [
  '[data-radix-portal]',
  '[data-radix-popper-content-wrapper]',
].join(', ');

const PORTAL_SCOPED_ATTR = 'data-nimbus-portal-scoped';
const PORTAL_BRIDGE_ATTRS = 'data-nimbus-portal-bridge-attrs';
const NimbusPortalBridgeContext = createContext<Record<string, string>>({});

const NIMBUS_PORTAL_VARIABLES = [
  '--nimbus-background',
  '--nimbus-foreground',
  '--nimbus-primary',
  '--nimbus-primary-foreground',
  '--nimbus-border',
  '--nimbus-muted',
  '--nimbus-muted-foreground',
  '--nimbus-secondary',
  '--nimbus-accent-foreground',
  '--nimbus-ring',
  '--nimbus-input',
  '--nimbus-destructive',
  '--nimbus-shadow-xs',
  '--nimbus-shadow-sm',
  '--nimbus-shadow-md',
  '--nimbus-shadow-lg',
  '--nimbus-shadow-switch-thumb',
  '--nimbus-shadow-tooltip',
  '--nimbus-shadow-badge-hover',
  '--nimbus-focus-outline-shadow',
  '--nimbus-focus-destructive-shadow',
  '--nimbus-focus-positive-shadow',
  '--nimbus-focus-blue-shadow',
  '--nimbus-focus-slider-shadow',
  '--nimbus-radius-xs',
  '--nimbus-radius-md',
  '--nimbus-radius-lg',
  '--shadow-nimbus-xs',
  '--shadow-nimbus-sm',
  '--shadow-nimbus-md',
  '--shadow-nimbus-lg',
  '--shadow-nimbus-switch-thumb',
  '--shadow-nimbus-tooltip',
  '--shadow-nimbus-badge-hover',
  '--shadow-nimbus-focus-outline',
  '--shadow-nimbus-focus-destructive',
  '--shadow-nimbus-focus-positive',
  '--shadow-nimbus-focus-blue',
  '--shadow-nimbus-focus-slider',
  '--radius-nimbus-xs',
  '--radius-nimbus-md',
  '--radius-nimbus-lg',
  '--background',
  '--foreground',
  '--card',
  '--card-foreground',
  '--popover',
  '--popover-foreground',
  '--primary',
  '--primary-foreground',
  '--secondary',
  '--secondary-foreground',
  '--muted',
  '--muted-foreground',
  '--accent',
  '--accent-foreground',
  '--destructive',
  '--border',
  '--input',
  '--ring',
  '--radius',
] as const;

export type Nimbus3ScopeTokens = Record<string, string>;

export type Nimbus3ScopeProps = {
  children: ReactNode;
  id?: string;
  theme?: 'light' | 'dark';
  tokens?: Nimbus3ScopeTokens;
  portalBridgeAttributes?: Record<string, string | undefined>;
  enablePortalBridge?: boolean;
  portalZIndex?: number | string;
  applyRadiusOverrides?: boolean;
  className?: string;
  style?: CSSProperties;
  scopeId?: string;
};

export function useNimbusPortalBridgeAttributes() {
  return useContext(NimbusPortalBridgeContext);
}

function mergeClassName(base: string, extra?: string) {
  if (!extra) {
    return base;
  }
  return `${base} ${extra}`;
}

function getPortalBridgeAttributeEntries(scopeRoot: HTMLElement) {
  const attributeNames = (
    scopeRoot.getAttribute(PORTAL_BRIDGE_ATTRS)?.split(',') ?? []
  )
    .map((attributeName) => attributeName.trim())
    .filter(Boolean);

  return attributeNames.flatMap((attributeName) => {
    const value = scopeRoot.getAttribute(attributeName);

    if (value === null) {
      return [];
    }

    return [[attributeName, value] as const];
  });
}

export function applyPortalStyles(
  portalNode: HTMLElement,
  scopeRoot: HTMLElement,
  portalZIndex: string
) {
  const computed = window.getComputedStyle(scopeRoot);

  for (const variable of NIMBUS_PORTAL_VARIABLES) {
    const value = computed.getPropertyValue(variable).trim();
    if (value.length > 0) {
      portalNode.style.setProperty(variable, value);
    }
  }

  portalNode.style.fontFamily = computed.fontFamily;
  portalNode.style.zIndex = portalZIndex;

  for (const [attributeName, value] of getPortalBridgeAttributeEntries(
    scopeRoot
  )) {
    portalNode.setAttribute(attributeName, value);
  }

  portalNode.setAttribute(PORTAL_SCOPED_ATTR, 'true');
}

function useNimbusPortalBridge(
  scopeSelector: string,
  enabled: boolean,
  portalZIndex: string
) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const win = window as Window & { __nimbusPortalBridgeInstalled?: boolean };
    if (win.__nimbusPortalBridgeInstalled) {
      return;
    }
    win.__nimbusPortalBridgeInstalled = true;

    let scheduled = false;
    const scheduleScan = () => {
      if (scheduled) {
        return;
      }
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const scopeRoot = document.querySelector<HTMLElement>(scopeSelector);
        if (!scopeRoot) {
          return;
        }

        for (const node of Array.from(document.body.children)) {
          if (!(node instanceof HTMLElement)) {
            continue;
          }
          if (node === scopeRoot || node.contains(scopeRoot)) {
            continue;
          }
          if (node.getAttribute(PORTAL_SCOPED_ATTR) === 'true') {
            continue;
          }
          if (
            !(
              node.matches(PORTAL_MARKERS) || node.querySelector(PORTAL_MARKERS)
            )
          ) {
            continue;
          }

          applyPortalStyles(node, scopeRoot, portalZIndex);
        }
      });
    };

    const observer = new MutationObserver(scheduleScan);
    observer.observe(document.body, { childList: true });
    scheduleScan();

    return () => {
      observer.disconnect();
      win.__nimbusPortalBridgeInstalled = false;
    };
  }, [enabled, scopeSelector, portalZIndex]);
}

const DEFAULT_RUNTIME_VARS: CSSProperties & Record<string, string> = {
  fontFamily:
    'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  '--background': 'var(--nimbus-background)',
  '--foreground': 'var(--nimbus-foreground)',
  '--card': 'var(--nimbus-background)',
  '--card-foreground': 'var(--nimbus-foreground)',
  '--popover': 'var(--nimbus-background)',
  '--popover-foreground': 'var(--nimbus-foreground)',
  '--primary': 'var(--nimbus-primary)',
  '--primary-foreground': 'var(--nimbus-primary-foreground)',
  '--secondary': 'var(--nimbus-secondary)',
  '--secondary-foreground': 'var(--nimbus-foreground)',
  '--muted': 'var(--nimbus-muted)',
  '--muted-foreground': 'var(--nimbus-muted-foreground)',
  '--accent': 'var(--nimbus-secondary)',
  '--accent-foreground': 'var(--nimbus-accent-foreground)',
  '--destructive': 'var(--nimbus-destructive)',
  '--border': 'var(--nimbus-border)',
  '--input': 'var(--nimbus-input)',
  '--ring': 'var(--nimbus-ring)',
  '--radius': '0.625rem',
  '--shadow-nimbus-xs': 'var(--nimbus-shadow-xs)',
  '--shadow-nimbus-sm': 'var(--nimbus-shadow-sm)',
  '--shadow-nimbus-md': 'var(--nimbus-shadow-md)',
  '--shadow-nimbus-lg': 'var(--nimbus-shadow-lg)',
  '--shadow-nimbus-switch-thumb': 'var(--nimbus-shadow-switch-thumb)',
  '--shadow-nimbus-tooltip': 'var(--nimbus-shadow-tooltip)',
  '--shadow-nimbus-badge-hover': 'var(--nimbus-shadow-badge-hover)',
  '--shadow-nimbus-focus-outline': 'var(--nimbus-focus-outline-shadow)',
  '--shadow-nimbus-focus-destructive': 'var(--nimbus-focus-destructive-shadow)',
  '--shadow-nimbus-focus-positive': 'var(--nimbus-focus-positive-shadow)',
  '--shadow-nimbus-focus-blue': 'var(--nimbus-focus-blue-shadow)',
  '--shadow-nimbus-focus-slider': 'var(--nimbus-focus-slider-shadow)',
  '--radius-nimbus-xs': 'var(--nimbus-radius-xs)',
  '--radius-nimbus-md': 'var(--nimbus-radius-md)',
  '--radius-nimbus-lg': 'var(--nimbus-radius-lg)',
};

const DEFAULT_RADIUS_OVERRIDES = `
  [data-nimbus-scope="true"] .rounded-sm { border-radius: calc(var(--radius) - 4px) !important; }
  [data-nimbus-scope="true"] .rounded-md { border-radius: calc(var(--radius) - 2px) !important; }
  [data-nimbus-scope="true"] .rounded-lg { border-radius: var(--radius) !important; }
  [data-nimbus-scope="true"] .rounded-xl { border-radius: calc(var(--radius) + 4px) !important; }
  [data-nimbus-scope="true"] .rounded-2xl { border-radius: calc(var(--radius) + 8px) !important; }
  [data-nimbus-scope="true"] .rounded-3xl { border-radius: calc(var(--radius) + 12px) !important; }
  [data-nimbus-scope="true"] .rounded-4xl { border-radius: calc(var(--radius) + 16px) !important; }
  [data-nimbus-scope="true"] .rounded-full { border-radius: calc(infinity * 1px) !important; }
`;

const DEFAULT_COMPAT_OVERRIDES = `
  [data-nimbus-scope="true"] button,
  [data-nimbus-scope="true"] input,
  [data-nimbus-scope="true"] select,
  [data-nimbus-scope="true"] textarea {
    letter-spacing: normal !important;
  }

  [data-nimbus-scope="true"] [class*="has-[>svg]:px-2.5"]:has(>svg) {
    padding-inline: calc(var(--spacing, 0.25rem) * 2.5) !important;
  }
  [data-nimbus-scope="true"] [class*="has-[>svg]:px-3"]:has(>svg) {
    padding-inline: calc(var(--spacing, 0.25rem) * 3) !important;
  }
  [data-nimbus-scope="true"] [class*="has-[>svg]:px-4"]:has(>svg) {
    padding-inline: calc(var(--spacing, 0.25rem) * 4) !important;
  }
  [data-nimbus-scope="true"] [class*="has-[>svg]:px-6"]:has(>svg) {
    padding-inline: calc(var(--spacing, 0.25rem) * 6) !important;
  }
`;

export default function Nimbus3Scope({
  children,
  id,
  theme = 'light',
  tokens,
  portalBridgeAttributes,
  enablePortalBridge = true,
  portalZIndex = '2147483000',
  applyRadiusOverrides = true,
  className,
  style,
  scopeId,
}: Nimbus3ScopeProps) {
  const runtimeStyle = useMemo(() => {
    const merged: CSSProperties & Record<string, string> = {
      ...DEFAULT_RUNTIME_VARS,
      ...(style as (CSSProperties & Record<string, string>) | undefined),
    };

    if (tokens) {
      for (const [key, value] of Object.entries(tokens)) {
        if (key.startsWith('--')) {
          merged[key] = value;
        } else {
          merged[`--${key}`] = value;
        }
      }
    }

    return merged;
  }, [style, tokens]);

  const resolvedPortalBridgeAttributes = useMemo<Record<string, string>>(() => {
    const resolvedAttributes: Record<string, string> = {};

    for (const [attributeName, value] of Object.entries(
      portalBridgeAttributes ?? {}
    )) {
      if (
        attributeName.startsWith('data-') &&
        typeof value === 'string' &&
        value.length > 0
      ) {
        resolvedAttributes[attributeName] = value;
      }
    }

    return resolvedAttributes;
  }, [portalBridgeAttributes]);
  const portalBridgeAttributeNames = useMemo(
    () => Object.keys(resolvedPortalBridgeAttributes),
    [resolvedPortalBridgeAttributes]
  );

  useNimbusPortalBridge(
    '[data-nimbus-scope="true"]',
    enablePortalBridge,
    `${portalZIndex}`
  );

  return (
    <NimbusPortalBridgeContext.Provider value={resolvedPortalBridgeAttributes}>
      <section
        id={id}
        data-nimbus-scope="true"
        data-nimbus-scope-id={scopeId}
        {...resolvedPortalBridgeAttributes}
        {...(portalBridgeAttributeNames.length > 0
          ? {
              [PORTAL_BRIDGE_ATTRS]: portalBridgeAttributeNames.join(','),
            }
          : {})}
        className={mergeClassName(theme === 'dark' ? 'dark' : '', className)}
        style={runtimeStyle}
      >
        {applyRadiusOverrides ? (
          <style>{DEFAULT_RADIUS_OVERRIDES}</style>
        ) : null}
        <style>{DEFAULT_COMPAT_OVERRIDES}</style>
        {children}
      </section>
    </NimbusPortalBridgeContext.Provider>
  );
}
